/**
 * 运营总览 · 实时业务看板 — /admin/analytics
 * 对齐 canvas「运营总览 - 实时看板」(83JbUq8HWZpj2VQ)·真订单聚合·WebSocket→v1 轮询刷新。
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Lock, RefreshCw } from "lucide-react";
import { analyticsApi } from "@/services/analytics";
import { PageShell, LoadingState } from "@/shared/ui/PageHeader";
import { OperationsTabs } from "./OperationsTabs";

const usd = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const CH_LABEL: Record<string, string> = { api: "🔌 API 平台", b2b: "🤝 同业 B2B", social: "📞 社媒/来电", client: "💻 客户端自助" };
const pctColor = (p: number) => (p >= 70 ? "bg-emerald-500" : p >= 40 ? "bg-amber-500" : "bg-rose-500");

export function AnalyticsDashboardPage() {
  const { data: d, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["analytics", "dashboard"],
    queryFn: analyticsApi.dashboard,
    refetchInterval: 5 * 60_000, // 5min 轮询（canvas WebSocket 的 v1 替代）
  });
  if (isLoading) return <LoadingState />;
  if (isError) return (
    <div className="flex flex-col items-center gap-2 py-16 text-[13px] text-slate-500">
      <span>运营总览加载失败</span>
      <button onClick={() => refetch()} className="rounded-md border border-slate-300 px-3 py-1 text-[12px] font-medium text-blue-600 hover:bg-blue-50">重试</button>
    </div>
  );
  if (!d) return <LoadingState />;

  const maxOrders = Math.max(1, ...d.trend.map((t) => t.orders));
  const maxGmv = Math.max(1, ...d.trend.map((t) => t.gmv_cents));
  const maxAvg = Math.max(1, ...d.trend.map((t) => t.avg_cents));

  return (
    <PageShell title="📊 运营总览 · 实时业务看板" subtitle="今日实时 · 老板/经理高频日常打开">
      <OperationsTabs />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
          <Lock size={14} /> 权限：老板看全部 · 经理看业务数据不看净利
        </div>
        <div className="flex items-center gap-3 text-[12px]">
          <span className="flex items-center gap-1 text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" /> 实时连接</span>
          <button onClick={() => refetch()} className="flex items-center gap-1 rounded-lg border border-slate-300 px-2.5 py-1 font-medium text-slate-600 hover:bg-slate-50"><RefreshCw size={13} className={isFetching ? "animate-spin" : ""} /> 手动刷新</button>
        </div>
      </div>

      {/* 6 KPI */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kpi label="今日订单" value={String(d.orders_today)} sub={`${d.orders_today_delta >= 0 ? "↗ +" : "↘ "}${d.orders_today_delta} vs 昨日`} />
        <Kpi label="今日新单 GMV" value={usd(d.gmv_today_cents)} sub={`${d.gmv_delta_pct >= 0 ? "↗ +" : "↘ "}${d.gmv_delta_pct}% vs 昨日`} accent />
        <Kpi label="今日已确认" value={String(d.confirmed_today)} sub={`${d.confirm_rate_pct}% 确认率`} />
        <Kpi label="待确认" value={String(d.pending_confirm)} sub="🟡 OP 处理中" tint="amber" />
        <Kpi label="待发凭证" value={String(d.pending_voucher)} sub="🟡 财务处理" tint="amber" />
        <Kpi label="异常告警" value={String(d.alert_count)} sub="🔴 见底部" tint={d.alert_count > 0 ? "rose" : undefined} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 团队状态 */}
        <Card title="👥 今日团队状态" foot={<Link to="/dispatch" className="text-[12px] font-medium text-blue-600">跳调度中心 →</Link>}>
          <div className="grid grid-cols-3 gap-2">
            <Mini label="🧑‍💼 OP 在岗" value={`${d.team.op_on} / ${d.team.op_total}`} />
            <Mini label="🚐 司导在岗" value={`${d.team.driver_on} / ${d.team.driver_total}`} />
            <Mini label="🚌 车辆在役" value={`${d.team.vehicle_active} / ${d.team.vehicle_total}`} />
          </div>
          <div className="mt-3 text-[12px] font-medium text-slate-600">今日出团：{d.today_tours.length} 团 / 共 {d.today_tours.reduce((a, t) => a + t.pax, 0)} 人</div>
          <ul className="mt-1 flex flex-col gap-1 text-[12px] text-slate-500">
            {d.today_tours.slice(0, 4).map((t) => <li key={t.order_no}>· {t.order_no} · {t.customer} · {t.pax} 人 · {t.status}</li>)}
            {d.today_tours.length === 0 && <li className="text-slate-400">今日无出团</li>}
          </ul>
        </Card>

        {/* 交接中心 */}
        <Card title="📋 今日交接中心摘要" foot={<Link to="/ops/handoff" className="text-[12px] font-medium text-blue-600">跳 OP 交接中心 →</Link>}>
          <div className="grid grid-cols-3 gap-2">
            <Mini label="🔴 待处理" value={String(d.handover.pending)} />
            <Mini label="✅ 已处理(今日)" value={String(d.handover.processed_today)} />
            <Mini label="⏱ 平均时长" value={`${d.handover.avg_process_min} min`} />
          </div>
          <div className="mt-3 text-[12px] font-medium text-slate-600">🏆 OP 处理排行：</div>
          <ul className="mt-1 flex flex-col gap-1 text-[12px] text-slate-500">
            {d.handover.ranking.map((r) => <li key={r.name}>· {r.name}：{r.count} 件 · 平均 {r.avg_min} min</li>)}
            {d.handover.ranking.length === 0 && <li className="text-slate-400">今日暂无处理记录</li>}
          </ul>
        </Card>

        {/* 库存健康 */}
        <Card title="📦 库存健康（3 资源综合可用性）" foot={<Link to="/products" className="text-[12px] font-medium text-blue-600">跳库存日历 →</Link>}>
          <div className="flex flex-col gap-3">
            {d.inventory_health.map((r) => (
              <div key={r.resource}>
                <div className="mb-1 text-[12px] font-medium text-slate-700">{r.resource}</div>
                <div className="flex gap-2">
                  {[["今", r.d0_pct], ["明", r.d1_pct], ["后", r.d2_pct]].map(([lbl, p]) => (
                    <div key={lbl as string} className="flex-1">
                      <div className="flex items-center justify-between text-[11px] text-slate-500"><span>{lbl}日</span><span className="font-semibold">{p}%</span></div>
                      <div className="mt-0.5 h-2 rounded-full bg-slate-100"><div className={`h-2 rounded-full ${pctColor(p as number)}`} style={{ width: `${p}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* 渠道分发 */}
        <Card title="🌐 渠道实时分发（今日订单）" foot={<Link to="/orders" className="text-[12px] font-medium text-blue-600">跳订单中心 →</Link>}>
          <div className="flex flex-col gap-2">
            {d.channel_distribution.map((c) => (
              <div key={c.channel}>
                <div className="flex items-center justify-between text-[12px]"><span className="text-slate-600">{CH_LABEL[c.channel] ?? c.channel}</span><span className="font-semibold text-slate-800">{c.pct}% · {c.orders} 单</span></div>
                <div className="mt-0.5 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-blue-500" style={{ width: `${c.pct}%` }} /></div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* 7 天趋势 */}
      <Card title="📈 近 7 天趋势（订单 + GMV + 客单）">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3">
            <div className="mb-2 text-[12px] font-semibold text-slate-700">📊 订单数</div>
            <div className="flex h-28 items-end gap-1.5">
              {d.trend.map((t) => (
                <div key={t.date} className="flex flex-1 flex-col items-center gap-1">
                  <div className="text-[10px] text-slate-500">{t.orders}</div>
                  <div className="w-full rounded-t bg-blue-500" style={{ height: `${(t.orders / maxOrders) * 80}px` }} />
                  <div className="text-[10px] text-slate-400">{t.date.slice(5)}</div>
                </div>
              ))}
            </div>
            <div className="mt-1 text-[11px] text-slate-500">累计 {d.trend.reduce((a, t) => a + t.orders, 0)} 单</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3">
            <div className="mb-2 text-[12px] font-semibold text-slate-700">💰 GMV (USD)</div>
            <div className="flex h-28 items-end gap-1.5">
              {d.trend.map((t) => (
                <div key={t.date} className="flex flex-1 flex-col items-center gap-1">
                  <div className="text-[10px] text-slate-500">{usd(t.gmv_cents)}</div>
                  <div className="w-full rounded-t bg-emerald-500" style={{ height: `${(t.gmv_cents / maxGmv) * 80}px` }} />
                  <div className="text-[10px] text-slate-400">{t.date.slice(5)}</div>
                </div>
              ))}
            </div>
            <div className="mt-1 text-[11px] text-slate-500">累计 {usd(d.trend.reduce((a, t) => a + t.gmv_cents, 0))}</div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-3">
            <div className="mb-2 text-[12px] font-semibold text-slate-700">💵 平均客单</div>
            <div className="flex h-28 items-end gap-1.5">
              {d.trend.map((t) => (
                <div key={t.date} className="flex flex-1 flex-col items-center gap-1">
                  <div className="text-[10px] text-slate-500">{usd(t.avg_cents)}</div>
                  <div className="w-full rounded-t bg-violet-500" style={{ height: `${(t.avg_cents / maxAvg) * 80}px` }} />
                  <div className="text-[10px] text-slate-400">{t.date.slice(5)}</div>
                </div>
              ))}
            </div>
            <div className="mt-1 text-[11px] text-slate-500">日均客单 {usd(Math.round(d.trend.reduce((a, t) => a + t.avg_cents, 0) / (d.trend.filter((t) => t.avg_cents > 0).length || 1)))}</div>
          </div>
        </div>
      </Card>

      {/* 异常告警摘要 */}
      <Card title="🚨 异常告警摘要（只看不操作 · 跳源模块处理）">
        {d.alerts.length === 0 ? (
          <div className="py-4 text-center text-[13px] text-emerald-600">✅ 暂无异常告警</div>
        ) : (
          <div className="flex flex-col gap-2">
            {d.alerts.map((a, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                <span className="text-lg">{a.level === "red" ? "🔴" : "🟡"}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-slate-800">{a.title}</div>
                  <div className="text-[11px] text-slate-500">来源：{a.source}</div>
                </div>
                <Link to={a.link} className="shrink-0 text-[12px] font-medium text-blue-600">处理 →</Link>
              </div>
            ))}
          </div>
        )}
      </Card>
    </PageShell>
  );
}

function Kpi({ label, value, sub, accent, tint }: { label: string; value: string; sub: string; accent?: boolean; tint?: "amber" | "rose" }) {
  const ring = tint === "rose" ? "border-rose-200 bg-rose-50" : tint === "amber" ? "border-amber-200 bg-amber-50/60" : accent ? "border-blue-200 bg-blue-50/60" : "border-slate-200 bg-white";
  return (
    <div className={`rounded-xl border p-4 ${ring}`}>
      <div className="text-[12px] text-slate-500">{label}</div>
      <div className="mt-1 text-[22px] font-bold text-slate-900">{value}</div>
      <div className="mt-0.5 text-[11px] text-slate-500">{sub}</div>
    </div>
  );
}
function Card({ title, foot, children }: { title: string; foot?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="flex flex-col rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 text-[14px] font-semibold text-slate-900">{title}</div>
      <div className="flex-1">{children}</div>
      {foot && <div className="mt-3 border-t border-slate-100 pt-2 text-right">{foot}</div>}
    </section>
  );
}
function Mini({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg bg-slate-50 px-2 py-2 text-center"><div className="text-[16px] font-bold text-slate-900">{value}</div><div className="text-[11px] text-slate-500">{label}</div></div>;
}
